/**
 * useConfigurationWorkflow - Unified bond and configuration workflow management
 *
 * Consolidates the common workflow pattern used by:
 * - handleQuickAddBond
 * - handleAddBond
 * - handleUpdateBond
 * - handleDeleteBond
 *
 * Pattern: validate → create/modify bonds → check matching config → process → save → notify
 */

import { useCallback, useMemo } from 'react';
import { BondSeriesBuilder } from '~/builders/bond-series/BondSeriesBuilder';
import {
  createBondFromConfiguration,
  createBondFromQuickInput,
  updateBondFromConfiguration,
} from '~/builders/bond-series/bond-series-factory';
import { toaster } from '~/components/ui/toaster';
import { createConfiguration } from '~/engine';
import { type ProjectData, useProjectStore } from '~/hooks/useProjectStore';
import {
  cloneConfigurationWithChanges,
  cloneConfigurationWithMillageOverrides,
  cloneConfigurationWithTaxableValueOverrides,
} from '~/services/configuration-operations';
import {
  checkForMatchingConfiguration,
  isNoViableCandidatesError,
  processConfigurationCandidates,
} from '~/services/configuration-processing';
import { hasAnyOverrides } from '~/services/override-service';
import type { BondSeries, Configuration, Project } from '~/types';
import type { BondConfiguration } from '~/types/bond-inputs';
import type { MillageOverrides, TaxableValueOverrides } from '~/types/overrides';
import { convertProjectDataToProject } from '~/utils/project-conversion';
import type { QuickBondInput } from '../AddBondModal';

// ============================================
// Types
// ============================================

/**
 * Input types for unified addBond function
 */
export type BondInput =
  | { type: 'quick'; data: QuickBondInput }
  | { type: 'full'; data: BondConfiguration };

/**
 * Bond operation types for applying changes to bond series
 */
export type BondOperation =
  | { type: 'add'; bond: BondSeries }
  | { type: 'update'; bondId: string; bond: BondSeries }
  | { type: 'remove'; bondId: string };

/**
 * Result of a bond operation (add, update, delete)
 */
export interface BondOperationResult {
  /** Whether the operation succeeded */
  success: boolean;
  /** What action was taken */
  action: 'created' | 'switched' | 'error';
  /** Configuration ID (for created/switched) */
  configurationId?: string;
  /** Number of candidates generated (for created) */
  candidateCount?: number;
  /** Processing time in ms (for created) */
  processingTimeMs?: number;
  /** Error message (for error) */
  error?: string;
  /** Whether this is a "no viable candidates" error */
  isNoViableCandidatesError?: boolean;
}

// ============================================
// Pure Helper Functions
// ============================================

/**
 * Apply a bond operation to the current bond series array
 * Returns a new array with the operation applied
 */
function applyBondOperation(currentSeries: BondSeries[], operation: BondOperation): BondSeries[] {
  switch (operation.type) {
    case 'add':
      return [...currentSeries, operation.bond];
    case 'update':
      return currentSeries.map((bond) => (bond.id === operation.bondId ? operation.bond : bond));
    case 'remove':
      return currentSeries.filter((bond) => bond.id !== operation.bondId);
  }
}

/**
 * Create a BondSeries from either quick or full input
 */
function createBondFromInput(
  input: BondInput,
  project: ProjectData,
  projectForFactory: Project
): BondSeries {
  if (input.type === 'quick') {
    return createBondFromQuickInput({ quickInput: input.data, project });
  }
  return createBondFromConfiguration({ configuration: input.data, project: projectForFactory });
}

interface UseConfigurationWorkflowProps {
  projectId: string;
  project: ProjectData;
  configurations: Configuration[] | null;
  selectedConfigurationId: string | null;
  onConfigurationCreated: (configId: string) => void;
  onCandidateSelected: (candidateId: string) => void;
  onModalClose: () => void;
}

/**
 * Shared processing logic - processes a configuration and generates candidates
 * Uses pure function for processing, handles store updates and UI feedback
 *
 * @param engineProject - Pre-converted Project (conversion should happen once at hook level)
 */
const processConfiguration = async (
  projectId: string,
  engineProject: Project,
  configuration: Configuration,
  onConfigurationCreated: (configId: string) => void,
  onCandidateSelected: (candidateId: string) => void,
  isNewConfiguration: boolean
): Promise<BondOperationResult> => {
  toaster.create({
    title: 'Processing Configuration',
    description: 'Analyzing all millage shapes and optimizing viable candidates...',
    type: 'loading',
    duration: 15000,
  });

  // Use pure function for processing (engineProject already converted)
  const result = processConfigurationCandidates(engineProject, configuration);

  if (!result.success) {
    toaster.dismiss();

    const noViableCandidates = isNoViableCandidatesError(result.error);

    toaster.create({
      title: noViableCandidates ? 'No Viable Bond Structures' : 'Failed to Process Configuration',
      description: noViableCandidates
        ? 'Unable to find workable solutions. Try reducing bond amounts, increasing peak millage, or adjusting DRF constraints.'
        : result.error,
      type: 'error',
      duration: noViableCandidates ? 15000 : 8000,
    });

    return {
      success: false,
      action: 'error',
      error: result.error,
      isNoViableCandidatesError: noViableCandidates,
    };
  }

  // Save to store
  if (isNewConfiguration) {
    useProjectStore.getState().addConfiguration(projectId, result.configuration);
  } else {
    useProjectStore.getState().updateConfiguration(projectId, result.configuration);
  }

  onConfigurationCreated(result.configuration.id);

  toaster.dismiss();
  toaster.create({
    title: 'Candidates Generated',
    description: `${result.candidateCount} optimized candidates generated in ${result.processingTimeMs}ms`,
    type: 'success',
    duration: 5000,
  });

  // Auto-select first candidate
  if (result.configuration.candidates && result.configuration.candidates.length > 0) {
    onCandidateSelected(result.configuration.candidates[0].id);
  }

  return {
    success: true,
    action: 'created',
    configurationId: result.configuration.id,
    candidateCount: result.candidateCount,
    processingTimeMs: result.processingTimeMs,
  };
};

/**
 * Check for matching configuration and switch to it if found
 * Uses pure function for matching, handles UI updates
 * IMPORTANT: Gets fresh configurations from store to avoid stale closure issues
 *
 * @returns BondOperationResult if match found, null otherwise
 */
const checkAndSwitchToMatchingConfig = (
  projectId: string,
  newBondSeries: BondSeries[],
  millageInputs: Configuration['millageInputs'],
  drfInputs: Configuration['drfInputs'],
  onConfigurationCreated: (configId: string) => void,
  onCandidateSelected: (candidateId: string) => void
): BondOperationResult | null => {
  // Get fresh configurations from store to avoid stale closure issues
  const freshConfigurations = useProjectStore.getState().projects[projectId]?.configurations || [];

  // Use pure function for matching
  const matchResult = checkForMatchingConfiguration(
    freshConfigurations,
    newBondSeries,
    millageInputs,
    drfInputs
  );

  if (matchResult.found && matchResult.configuration) {
    const existingConfig = matchResult.configuration;
    onConfigurationCreated(existingConfig.id);
    if (existingConfig.candidates && existingConfig.candidates.length > 0) {
      onCandidateSelected(existingConfig.candidates[0].id);
    }
    toaster.create({
      title: 'Using existing configuration',
      description: `Configuration ${existingConfig.orderId} matches`,
      type: 'success',
      duration: 3000,
    });
    return {
      success: true,
      action: 'switched',
      configurationId: existingConfig.id,
      candidateCount: existingConfig.candidates?.length || 0,
    };
  }
  return null;
};

export const useConfigurationWorkflow = ({
  projectId,
  project,
  configurations,
  selectedConfigurationId,
  onConfigurationCreated,
  onCandidateSelected,
  onModalClose,
}: UseConfigurationWorkflowProps) => {
  const currentConfig = configurations?.find((c) => c.id === selectedConfigurationId) || null;

  // Convert once, memoized - avoids repeated conversion in every method
  const engineProject = useMemo(() => convertProjectDataToProject(project, true), [project]);

  /**
   * Initialize a bond with payment map aligned to project dates
   */
  const initializeBond = useCallback(
    (bondSeries: BondSeries): BondSeries => {
      return BondSeriesBuilder.initializeBondSeries(bondSeries, engineProject);
    },
    [engineProject]
  );

  /**
   * Create first configuration (when no configurations exist)
   */
  const createFirstConfiguration = useCallback(
    async (initializedBond: BondSeries): Promise<BondOperationResult> => {
      const newConfiguration = createConfiguration(engineProject, [initializedBond]);

      return processConfiguration(
        projectId,
        engineProject,
        newConfiguration,
        onConfigurationCreated,
        onCandidateSelected,
        true
      );
    },
    [engineProject, projectId, onConfigurationCreated, onCandidateSelected]
  );

  /**
   * Create configuration with new bond series
   *
   * IMPORTANT: If the source configuration has overrides (taxable value or millage),
   * we must use cloneConfigurationWithChanges to preserve them. This ensures that
   * custom millage rates are maintained when adding/updating/deleting bonds,
   * which means only ONE candidate will be generated (the custom shape path).
   *
   * This follows a "git branch" model - once you customize, you stay on that branch.
   * To go back to all millage shapes, start from a config without overrides.
   */
  const createConfigurationWithBonds = useCallback(
    async (newBondSeries: BondSeries[]): Promise<BondOperationResult> => {
      let newConfiguration: Configuration;

      // If current config has overrides, clone to preserve them
      // This ensures custom millage/taxable value overrides are inherited
      if (currentConfig && hasAnyOverrides(currentConfig.overrides)) {
        const result = cloneConfigurationWithChanges(
          engineProject,
          currentConfig.id,
          { bondSeries: newBondSeries }
          // Note: NOT passing overrides here, so they'll be inherited from base config
        );

        if (!result) {
          return {
            success: false,
            action: 'error',
            error: 'Failed to clone configuration with overrides',
          };
        }

        // Find the new configuration from the updated project
        newConfiguration = result.updatedProject.configurations?.find(
          (c) => c.id === result.newConfigurationId
        ) as Configuration;
      } else {
        // No overrides - create fresh configuration (will iterate all shapes)
        newConfiguration = createConfiguration(engineProject, newBondSeries);
      }

      return processConfiguration(
        projectId,
        engineProject,
        newConfiguration,
        onConfigurationCreated,
        onCandidateSelected,
        true
      );
    },
    [engineProject, projectId, currentConfig, onConfigurationCreated, onCandidateSelected]
  );

  /**
   * Unified add bond function - handles both quick and full inputs
   */
  const addBond = useCallback(
    async (input: BondInput) => {
      try {
        if (!project.dateElements?.dateRecordsMapByPaymentDate) {
          throw new Error(
            'Date maps not found. Please complete Project Setup before adding bonds.'
          );
        }

        // Create and initialize bond using unified helper
        const bondSeries = createBondFromInput(input, project, engineProject);
        const initializedBond = initializeBond(bondSeries);

        // First bond case
        if (!currentConfig) {
          await createFirstConfiguration(initializedBond);
          onModalClose();
          return;
        }

        // Apply operation to get new bond series
        const newBondSeries = applyBondOperation(currentConfig.bondSeries, {
          type: 'add',
          bond: initializedBond,
        });

        // Check for matching config
        const matchResult = checkAndSwitchToMatchingConfig(
          projectId,
          newBondSeries,
          currentConfig.millageInputs,
          currentConfig.drfInputs,
          onConfigurationCreated,
          onCandidateSelected
        );
        if (matchResult) {
          onModalClose();
          return;
        }

        // Create new configuration
        await createConfigurationWithBonds(newBondSeries);
        onModalClose();
      } catch (error) {
        toaster.create({
          title: 'Error Adding Bond',
          description: error instanceof Error ? error.message : 'Failed to add bond',
          type: 'error',
          duration: 5000,
        });
      }
    },
    [
      project,
      engineProject,
      projectId,
      currentConfig,
      initializeBond,
      createFirstConfiguration,
      createConfigurationWithBonds,
      onConfigurationCreated,
      onCandidateSelected,
      onModalClose,
    ]
  );

  /**
   * Convenience wrapper for quick add bond (maintains backward compatibility)
   */
  const addBondQuick = useCallback(
    async (quickData: QuickBondInput) => {
      return addBond({ type: 'quick', data: quickData });
    },
    [addBond]
  );

  /**
   * Convenience wrapper for full add bond (maintains backward compatibility)
   */
  const addBondFull = useCallback(
    async (config: BondConfiguration) => {
      return addBond({ type: 'full', data: config });
    },
    [addBond]
  );

  /**
   * Update an existing bond in the current configuration
   * Uses factory function for consistent bond construction
   */
  const updateBond = useCallback(
    async (bondId: string, config: BondConfiguration) => {
      try {
        if (!currentConfig) return;

        const existingBond = currentConfig.bondSeries.find((b) => b.id === bondId);
        if (!existingBond) return;

        // Use factory function for consistent bond construction
        const updatedBondBase = updateBondFromConfiguration({
          existingBond,
          configuration: config,
          project: engineProject,
        });

        // Re-initialize the bond to rebuild bondPaymentMap with updated date range
        // This is critical when duration changes - otherwise extended payment dates
        // won't have entries in bondPaymentMap and won't be marked as active
        const updatedBond = initializeBond(updatedBondBase);

        // Apply operation using helper
        const newBondSeries = applyBondOperation(currentConfig.bondSeries, {
          type: 'update',
          bondId,
          bond: updatedBond,
        });

        // Check for matching config
        const matchResult = checkAndSwitchToMatchingConfig(
          projectId,
          newBondSeries,
          currentConfig.millageInputs,
          currentConfig.drfInputs,
          onConfigurationCreated,
          onCandidateSelected
        );
        if (matchResult) {
          // checkAndSwitchToMatchingConfig already shows toast
          onModalClose();
          return;
        }

        // Create new configuration with updated bond
        await createConfigurationWithBonds(newBondSeries);
        onModalClose();
      } catch (error) {
        toaster.dismiss();
        toaster.create({
          title: 'Failed to Update Bond',
          description: error instanceof Error ? error.message : 'Unknown error occurred',
          type: 'error',
          duration: 5000,
        });
      }
    },
    [
      engineProject,
      projectId,
      currentConfig,
      initializeBond,
      createConfigurationWithBonds,
      onConfigurationCreated,
      onCandidateSelected,
      onModalClose,
    ]
  );

  /**
   * Delete a bond from the current configuration
   */
  const deleteBond = useCallback(
    async (bondId: string) => {
      try {
        if (!currentConfig) return;

        // Can't remove last bond
        if (currentConfig.bondSeries.length === 1) {
          toaster.create({
            title: 'Cannot remove last bond',
            description: 'A configuration must have at least one bond',
            type: 'warning',
            duration: 3000,
          });
          return;
        }

        // Apply operation using helper
        const newBondSeries = applyBondOperation(currentConfig.bondSeries, {
          type: 'remove',
          bondId,
        });

        // Check for matching config
        const matchResult = checkAndSwitchToMatchingConfig(
          projectId,
          newBondSeries,
          currentConfig.millageInputs,
          currentConfig.drfInputs,
          onConfigurationCreated,
          onCandidateSelected
        );
        if (matchResult) {
          return;
        }

        // Create new configuration
        await createConfigurationWithBonds(newBondSeries);
      } catch (error) {
        toaster.create({
          title: 'Failed to Delete Bond',
          description: error instanceof Error ? error.message : 'Unknown error occurred',
          type: 'error',
          duration: 5000,
        });
      }
    },
    [
      projectId,
      currentConfig,
      createConfigurationWithBonds,
      onConfigurationCreated,
      onCandidateSelected,
    ]
  );

  /**
   * Reprocess current configuration with updated millage inputs
   * Follows the same pattern as bond operations for consistency
   *
   * OVERRIDE BEHAVIOR:
   * - Taxable value overrides: PRESERVED (independent of millage structure)
   * - Millage rate overrides: DROPPED (changing millage inputs invalidates existing rate overrides)
   */
  const reprocessWithMillage = useCallback(
    async (
      updatedMillageInputs: Configuration['millageInputs']
    ): Promise<BondOperationResult | null> => {
      if (!currentConfig) return null;

      try {
        // Check for matching configuration first (same pattern as bond operations)
        const matchResult = checkAndSwitchToMatchingConfig(
          projectId,
          currentConfig.bondSeries,
          updatedMillageInputs,
          currentConfig.drfInputs,
          onConfigurationCreated,
          onCandidateSelected
        );
        if (matchResult) {
          return matchResult;
        }

        // No match found - create new configuration with updated millage
        // Preserve taxable value overrides but drop millage overrides
        // (changing millage inputs invalidates millage rate overrides)
        const taxableValueOverridesToPreserve = currentConfig.overrides?.taxableValue ?? null;
        const overridesToApply = taxableValueOverridesToPreserve
          ? { taxableValue: taxableValueOverridesToPreserve, millage: null }
          : null;

        // Clone configuration with millage input changes and preserved TV overrides
        const result = cloneConfigurationWithChanges(engineProject, currentConfig.id, {
          millageInputs: updatedMillageInputs,
          overrides: overridesToApply,
        });

        if (!result) {
          return {
            success: false,
            action: 'error',
            error: 'Failed to create configuration with updated millage inputs',
          };
        }

        // Find the new configuration from the result
        const newConfiguration = result.updatedProject.configurations?.find(
          (c) => c.id === result.newConfigurationId
        ) as Configuration;

        // Process and save (same pattern as bond operations)
        return processConfiguration(
          projectId,
          engineProject,
          newConfiguration,
          onConfigurationCreated,
          onCandidateSelected,
          true // isNewConfiguration
        );
      } catch (error) {
        toaster.dismiss();
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        const noViableCandidates = isNoViableCandidatesError(errorMessage);

        toaster.create({
          title: noViableCandidates
            ? 'No Viable Bond Structures'
            : 'Failed to Process Configuration',
          description: noViableCandidates
            ? 'Unable to find workable solutions. Try reducing bond amounts, increasing peak millage, or adjusting DRF constraints.'
            : errorMessage,
          type: 'error',
          duration: noViableCandidates ? 15000 : 8000,
        });

        return {
          success: false,
          action: 'error',
          error: errorMessage,
          isNoViableCandidatesError: noViableCandidates,
        };
      }
    },
    [engineProject, projectId, currentConfig, onConfigurationCreated, onCandidateSelected]
  );

  /**
   * Create a new configuration with taxable value overrides applied
   * Used when user customizes taxable values via the modal
   */
  const createWithTaxableValueOverrides = useCallback(
    async (overrides: TaxableValueOverrides): Promise<BondOperationResult | null> => {
      if (!currentConfig) return null;

      try {
        // Clone configuration with overrides (name is set automatically by the factory)
        const result = cloneConfigurationWithTaxableValueOverrides(
          engineProject,
          currentConfig.id,
          overrides
        );

        if (!result) {
          toaster.create({
            title: 'Failed to Create Configuration',
            description: 'Could not apply taxable value overrides',
            type: 'error',
            duration: 5000,
          });
          return {
            success: false,
            action: 'error',
            error: 'Could not apply taxable value overrides',
          };
        }

        // Process the new configuration (same pattern as other operations)
        return processConfiguration(
          projectId,
          engineProject,
          result.updatedProject.configurations?.find(
            (c) => c.id === result.newConfigurationId
          ) as Configuration,
          onConfigurationCreated,
          onCandidateSelected,
          true // isNewConfiguration
        );
      } catch (error) {
        toaster.dismiss();
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        toaster.create({
          title: 'Failed to Apply Overrides',
          description: errorMessage,
          type: 'error',
          duration: 8000,
        });

        return {
          success: false,
          action: 'error',
          error: errorMessage,
        };
      }
    },
    [engineProject, projectId, currentConfig, onConfigurationCreated, onCandidateSelected]
  );

  /**
   * Create a new configuration with millage rate overrides applied
   * Used when user customizes millage rates via the modal
   */
  const createWithMillageOverrides = useCallback(
    async (overrides: MillageOverrides): Promise<BondOperationResult | null> => {
      if (!currentConfig) return null;

      try {
        // Clone configuration with overrides (name is set automatically by the factory)
        const result = cloneConfigurationWithMillageOverrides(
          engineProject,
          currentConfig.id,
          overrides
        );

        if (!result) {
          toaster.create({
            title: 'Failed to Create Configuration',
            description: 'Could not apply millage rate overrides',
            type: 'error',
            duration: 5000,
          });
          return {
            success: false,
            action: 'error',
            error: 'Could not apply millage rate overrides',
          };
        }

        // Process the new configuration (same pattern as other operations)
        return processConfiguration(
          projectId,
          engineProject,
          result.updatedProject.configurations?.find(
            (c) => c.id === result.newConfigurationId
          ) as Configuration,
          onConfigurationCreated,
          onCandidateSelected,
          true // isNewConfiguration
        );
      } catch (error) {
        toaster.dismiss();
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        toaster.create({
          title: 'Failed to Apply Overrides',
          description: errorMessage,
          type: 'error',
          duration: 8000,
        });

        return {
          success: false,
          action: 'error',
          error: errorMessage,
        };
      }
    },
    [engineProject, projectId, currentConfig, onConfigurationCreated, onCandidateSelected]
  );

  return {
    addBondQuick,
    addBondFull,
    updateBond,
    deleteBond,
    reprocessWithMillage,
    createWithTaxableValueOverrides,
    createWithMillageOverrides,
    currentConfig,
  };
};
